import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Home,
  Search,
  MessageCircle,
  Users,
  Calendar,
  Settings,
  LogOut,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { NotificationDropdown } from '@/components/NotificationDropdown'
import { useAuth } from '@/hooks/useAuth'
import { getInitials } from '@/lib/utils'

const navItems = [
  { icon: Home, label: 'Feed', path: '/feed' },
  { icon: Search, label: 'Explore', path: '/explore' },
  { icon: MessageCircle, label: 'Messages', path: '/messages' },
  { icon: Users, label: 'Groups', path: '/groups' },
  { icon: Calendar, label: 'Events', path: '/events' },
]

export function MainLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex min-h-screen bg-[#0A0A0F] text-[#FAFAFA]">
      {/* Sidebar - Desktop */}
      <aside className="hidden w-64 flex-col border-r border-[#1E1E28] bg-[#12121A] lg:flex">
        <div className="flex h-16 items-center border-b border-[#1E1E28] px-6">
          <img
            src="/logos/logo_dark_mark_1024.png"
            alt="KLASSIC"
            className="h-8 w-auto mr-2"
          />
          <span className="font-display text-xl font-bold gradient-text">KLASSIC</span>
        </div>

        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-[#FF2D7B]/10 text-[#FF2D7B] shadow-[0_0_20px_rgba(255,45,123,0.15)]'
                    : 'text-[#A0A0B0] hover:bg-[#1E1E28] hover:text-[#FAFAFA]'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-[#1E1E28] p-4">
          <div className="flex items-center justify-between mb-3 px-2">
            <NotificationDropdown />
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#A0A0B0] hover:text-[#FF2D7B] hover:bg-[#FF2D7B]/10">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Avatar className="border-2 border-[#FF2D7B]/30">
              <AvatarImage src={user?.avatarUrl} alt={user?.displayName} />
              <AvatarFallback className="bg-[#1E1E28] text-[#FAFAFA]">{getInitials(user?.displayName || 'U')}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium text-[#FAFAFA]">{user?.displayName}</p>
              <p className="truncate text-xs text-[#64647A]">@{user?.username}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Top header - Mobile */}
        <header className="flex h-16 items-center justify-between border-b border-[#1E1E28] bg-[#12121A] px-4 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/logos/logo_dark_mark_1024.png" alt="KLASSIC" className="h-8 w-auto" />
            <span className="font-display text-lg font-bold gradient-text">KLASSIC</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationDropdown />
            <NavLink to="/profile">
              <Avatar className="h-8 w-8 border-2 border-[#FF2D7B]/30">
                <AvatarImage src={user?.avatarUrl} alt={user?.displayName} />
                <AvatarFallback className="bg-[#1E1E28] text-[#FAFAFA]">{getInitials(user?.displayName || 'U')}</AvatarFallback>
              </Avatar>
            </NavLink>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto bg-[#0A0A0F]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto max-w-3xl p-4 pb-20 lg:p-8 lg:pb-8"
          >
            <Outlet />
          </motion.div>
        </main>

        {/* Bottom navigation - Mobile */}
        <nav className="fixed bottom-0 left-0 right-0 flex justify-around border-t border-[#1E1E28] bg-[#12121A] py-2 lg:hidden">
          {navItems.slice(0, 4).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-1 p-2 text-xs transition-colors',
                  isActive ? 'text-[#FF2D7B]' : 'text-[#64647A]'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 p-2 text-xs transition-colors',
                isActive ? 'text-[#FF2D7B]' : 'text-[#64647A]'
              )
            }
          >
            <User className="h-5 w-5" />
            <span>Profile</span>
          </NavLink>
        </nav>
      </div>

      {/* Right sidebar - Desktop (for suggestions, trends, etc.) */}
      <aside className="hidden w-80 flex-col border-l border-[#1E1E28] bg-[#12121A] xl:flex">
        <div className="p-6">
          <h2 className="font-display text-lg font-semibold text-[#FAFAFA]">Trending</h2>
          <p className="mt-2 text-sm text-[#64647A]">
            Discover what&apos;s happening in your network
          </p>
        </div>
        <div className="flex-1 px-6">
          {/* Trending topics placeholder */}
          <div className="space-y-4">
            {[
              { category: 'Music', tag: '#SummerVibes', posts: '1.2K', color: '#FF2D7B' },
              { category: 'Art', tag: '#DigitalArt', posts: '890', color: '#6B3FA0' },
              { category: 'Photography', tag: '#GoldenHour', posts: '654', color: '#00D4FF' },
            ].map((trend, i) => (
              <div key={i} className="space-y-1 p-3 rounded-xl hover:bg-[#1E1E28] transition-colors cursor-pointer">
                <p className="text-xs text-[#64647A]">Trending in {trend.category}</p>
                <p className="font-medium text-[#FAFAFA]" style={{ color: trend.color }}>{trend.tag}</p>
                <p className="text-xs text-[#64647A]">{trend.posts} posts</p>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-[#1E1E28] p-6">
          <NavLink to="/settings">
            <Button variant="ghost" className="w-full justify-start gap-2 text-[#A0A0B0] hover:text-[#FAFAFA] hover:bg-[#1E1E28]">
              <Settings className="h-4 w-4" />
              Settings
            </Button>
          </NavLink>
        </div>
      </aside>
    </div>
  )
}
